import React, { useRef, useEffect, useState } from 'react'
import * as d3 from 'd3'
import { rounded_rect } from '../../../utils/graphUtils'
import { convertUnitAndValue } from '../../../utils/utils'
import { UNITS } from '../../../types/misc.types'

interface DataPoint {
  x_value: number
  y_value: number
  label: number | null
}

interface GroupedBarChartProps {
  data1: DataPoint[]
  data2: DataPoint[]
}

type GroupPoint = {
  x_value: number
  currFunds: number
  benchmarkFunds: number
  currLabel: number | null
  benchmarkLabel: number | null
}

const GroupedBarChart: React.FC<GroupedBarChartProps> = ({ data1, data2 }) => {
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 })

  useEffect(() => {
    const resizeListener = () => {
      if (svgRef.current) {
        const { width, height } = svgRef.current.getBoundingClientRect()
        setDimensions({ width, height })
      }
    }
    window.addEventListener('resize', resizeListener)
    resizeListener()
    return () => window.removeEventListener('resize', resizeListener)
  }, [])

  const upArrow = `<svg xmlns="http://www.w3.org/2000/svg" width="6" height="5" viewBox="0 0 6 5" fill="none">
  <path d="M3 0.523437L0 4.52344H6L3 0.523437Z" fill="#188659"/>
</svg>`

  const downArrow = `<svg xmlns="http://www.w3.org/2000/svg" width="6" height="5" viewBox="0 0 6 5" fill="none">
  <path d="M3 4.5L6 0.5L3.49691e-07 0.499999L3 4.5Z" fill="#E77464"/>
</svg>`

  const negativeDownArrow = `<svg xmlns="http://www.w3.org/2000/svg" width="6" height="5" viewBox="0 0 6 5" fill="none">
  <path d="M3 4.5L6 0.5L3.49691e-07 0.499999L3 4.5Z" fill="#E77464"/>
</svg>`

  useEffect(() => {
    if (!svgRef.current || dimensions.width === 0 || dimensions.height === 0) return

    const margin = { top: 10, right: 10, bottom: 30, left: 0 }
    const width = dimensions.width - margin.left - margin.right
    const height = dimensions.height - margin.top - margin.bottom

    const svg = d3
      .select(svgRef.current)
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${dimensions.width} ${dimensions.height}`)

    svg.selectAll('*').remove()

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`)

    const allData = [...data1, ...data2]
    const minY = d3.min(allData, (d) => d3.min([d?.y_value ?? 0, 0])) as number
    const maxY = d3.max(allData, (d) => d3.max([d?.y_value ?? 0, 0])) as number

    const rangeY = maxY - minY

    const paddingPixels = 60
    const paddingPercentage = paddingPixels / height
    const paddingAmount = rangeY * paddingPercentage

    const hasNegativeValue = data1.some((d) => d.y_value < 0) || data2.some((d) => d.y_value < 0)

    const y = d3
      .scaleLinear()
      .domain([minY - (hasNegativeValue ? paddingAmount * 1.25 : 0), maxY + paddingAmount])
      .nice()
      .rangeRound([height, 0])

    const groupedData = data1.reduce((result: GroupPoint[], d1) => {
      const d2 = data2.find((d) => d?.x_value === d1?.x_value)
      if (d2) {
        result.push({
          x_value: d1?.x_value ?? 0,
          currFunds: d1?.y_value ?? 0,
          benchmarkFunds: d2?.y_value ?? 0,
          currLabel: d1?.label ?? null,
          benchmarkLabel: d2?.label ?? null
        } as GroupPoint)
      }

      return result
    }, [])

    const numGroups = groupedData.length

    let barWidth = 40
    let gapBetweenBars = 7
    let yFontSize = 12
    let labelFontSize = 11
    if (width < 350) {
      gapBetweenBars = 5
      barWidth = 35
      yFontSize = 11
      labelFontSize = 10
    } else if (width < 280) {
      gapBetweenBars = 3
      barWidth = 30
      yFontSize = 10
      labelFontSize = 9
    }

    const totalBarWidth = numGroups * 2 * barWidth + (numGroups - 1) * gapBetweenBars
    const availableWidth = width
    const remainingWidthForGaps = availableWidth - totalBarWidth
    const gapBetweenGroups = (() => {
      if (numGroups === 1) return 0
      if (numGroups === 2) return 50
      return remainingWidthForGaps / (numGroups - 1)
    })()

    if (hasNegativeValue) {
      const gradient = svg
        .append('defs')
        .append('linearGradient')
        .attr('id', 'area-gradient')
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '0%')
        .attr('y2', '100%')

      gradient.append('stop').attr('offset', '0%').attr('stop-color', '#FBEFEF').attr('stop-opacity', 1)

      gradient.append('stop').attr('offset', '50%').attr('stop-color', '#FBEFEF').attr('stop-opacity', 0)

      g.append('path')
        .attr('fill', 'url(#area-gradient)')
        .attr('d', `M0,${y(0)} L${width + 7},${y(0)} L${width + 7},${height} L0,${height} Z`)
    }

    const getOffsetForGroupPlacement = () => {
      if (numGroups > 2) return 0
      return (width - (numGroups * (2 * barWidth + gapBetweenBars + gapBetweenGroups) - gapBetweenGroups)) / 2
    }

    const groups = g
      .selectAll('.group')
      .data(groupedData)
      .enter()
      .append('g')
      .attr('class', (d, i) => `group${i + 1}`)
      .attr('transform', (d, i) => {
        return `translate(${i * (2 * barWidth + gapBetweenBars + gapBetweenGroups) + getOffsetForGroupPlacement()},0)`
      })

    groups
      .append('path')
      .attr('d', (d) => {
        const rectHeight = Math.abs(y(d?.currFunds) - y(0))
        const topLeft = d?.currFunds >= 0
        const bottomLeft = d?.currFunds < 0
        const topRight = d?.currFunds >= 0
        const bottomRight = d?.currFunds < 0
        return rounded_rect(
          0,
          y(Math.max(0, d?.currFunds)),
          barWidth,
          rectHeight,
          4,
          topLeft,
          topRight,
          bottomLeft,
          bottomRight
        )
      })
      .attr('fill', '#1470CC')
      .each(function (d) {
        const barHeight = Math.abs(y(d?.currFunds) - y(0))
        const labelY = d?.currFunds >= 0 ? y(d?.currFunds) - 7 : y(d?.currFunds) + 17

        d3.select(this.parentNode as SVGGElement)
          .append('text')
          .attr('x', barWidth / 2 + 2)
          .attr('y', labelY)
          .attr('font-weight', 500)
          .attr('font-size', yFontSize)
          .attr('text-anchor', 'middle')
          .attr('fill', () => '#232323')
          .text(
            `${convertUnitAndValue({
              value: d?.currFunds,
              unit: UNITS.PERCENTAGE
            })}`
          )

        let arrow = d?.currLabel && d?.currLabel >= 0 ? upArrow : downArrow
        let fontColor = d?.currLabel && d?.currLabel >= 0 ? '#0B6B2A' : '#E77464'
        if (d?.currFunds < 0) {
          arrow = negativeDownArrow
          fontColor = '#E77464'
        }
        const labelPositionY = d?.currFunds >= 0 ? y(d?.currFunds) - 39 : y(d?.currFunds) + 19
        d3.select(this.parentNode as SVGGElement)
          .append('foreignObject')
          .attr('class', 'label')
          .attr('x', barWidth / 2 - 17)
          .attr('y', labelPositionY)
          .attr('width', 60)
          .attr('height', 20)
          .html(
            () => `
            <div style="display: flex; align-items: center; justify-content: start; 
            gap: 3px; font-size: ${labelFontSize}px; font-weight: 700; color: ${fontColor};">
              <div style="margin-top: 2px;">${arrow}</div><div>${
                d?.currLabel !== null
                  ? `${convertUnitAndValue({
                      value: Math.abs(d?.currLabel),
                      unit: UNITS.PERCENTAGE
                    })}`
                  : '--'
              }</div>
            </div>
          `
          )
      })

    groups
      .append('path')
      .attr('d', (d) => {
        const rectHeight = Math.abs(y(d?.benchmarkFunds) - y(0))
        const topLeft = d?.benchmarkFunds >= 0
        const bottomLeft = d?.benchmarkFunds < 0
        const topRight = d?.benchmarkFunds >= 0
        const bottomRight = d?.benchmarkFunds < 0
        return rounded_rect(
          barWidth + gapBetweenBars,
          y(Math.max(0, d?.benchmarkFunds)),
          barWidth,
          rectHeight,
          4,
          topLeft,
          topRight,
          bottomLeft,
          bottomRight
        )
      })
      .attr('fill', '#E56832')
      .each(function (d) {
        const labelY = (d?.benchmarkFunds ?? 0) >= 0 ? y(d?.benchmarkFunds ?? 0) - 7 : y(d?.benchmarkFunds ?? 0) + 17
        d3.select(this.parentNode as SVGGElement)
          .append('text')
          .attr('x', barWidth + gapBetweenBars + barWidth / 2 + 2)
          .attr('y', labelY)
          .attr('font-weight', 500)
          .attr('font-size', yFontSize)
          .attr('text-anchor', 'middle')
          .attr('fill', '#232323')
          .text(
            `${convertUnitAndValue({
              value: d?.benchmarkFunds,
              unit: UNITS.PERCENTAGE
            })}`
          )
      })

    // g.append('g').call(d3.axisLeft(y))

    svg
      .append('line')
      .attr('x1', 0)
      .attr('y1', hasNegativeValue ? height + 10 : y(0) + 10)
      .attr('x2', width + 7)
      .attr('y2', hasNegativeValue ? height + 10 : y(0) + 10)
      .attr('stroke', '#D1D6D6')
      .attr('stroke-width', 0.5)

    groups.each(function (d, i) {
      const xPos = i * (2 * barWidth + gapBetweenBars + gapBetweenGroups) + barWidth + getOffsetForGroupPlacement() + 5
      d3.select(this.parentNode as SVGGElement)
        .append('text')
        .attr('x', xPos)
        .attr('y', hasNegativeValue ? height + 20 : y(0) + 20)
        .attr('text-anchor', 'middle')
        .attr('font-size', '12px')
        .attr('fill', '#6A6E6E')
        .text(`${d?.x_value}Y Rolling`)
    })
  }, [data1, data2, dimensions])

  return (
    <div
      className='h-full w-full'
      role='img'
      aria-label='Rolling returns comparison bar chart for selected fund versus category'
    >
      <svg ref={svgRef} style={{ width: '100%', height: '100%' }} aria-hidden='true' />
    </div>
  )
}

export default GroupedBarChart
